import * as React from "react"
import { useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Icons } from "@/components/icons"
import { Button } from "@/components/ui/button.tsx"
import { supabase } from "@/services/supabase/supabase.ts"
import { Loader2, ArrowRight } from "lucide-react"
import { useAuth } from "@/features/auth"
import { motion } from "framer-motion"

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> { }

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const navigate = useNavigate();
    const { session } = useAuth();

    React.useEffect(() => {
        // Check for the OAuth response
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        if (error) {
            console.error('Auth Error:', error, errorDescription);
            return;
        }

        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
            // Set the session manually if we have tokens from OAuth
            supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            }).then(({ data: { session } }) => {
                if (session) {
                    navigate('/projects', { replace: true });
                }
            }).catch(error => {
                console.error('Error setting session:', error);
            });
        } else if (session) {
            navigate('/projects', { replace: true });
        }
    }, [session, navigate]);

    async function onSubmit(event: React.SyntheticEvent) {
        event.preventDefault()
        setIsLoading(true)

        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth`,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent'
                    }
                }
            });

            if (error) {
                console.error('Authentication error:', error);
            } else {
                // If we have a URL to redirect to, do it
                if (data?.url) {
                    window.location.href = data.url;
                }
            }
        } catch (error) {
            console.error('Unexpected error:', error);
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={cn("grid gap-6", className)} {...props}>
            <form onSubmit={onSubmit}>
                <div className="grid gap-4">
                    <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Button
                            variant="outline"
                            type="submit"
                            disabled={isLoading}
                            className="w-full h-12 text-base font-medium border-zinc-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all duration-200 relative overflow-hidden group"
                        >
                            <div className="flex items-center justify-center w-full">
                                {isLoading ? (
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                ) : (
                                    <Icons.google className="mr-2 h-5 w-5" />
                                )}
                                <span className="mr-2">Continue with Google</span>
                                <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                            </div>
                        </Button>
                    </motion.div>
                </div>
            </form>

        </div>
    )
}
